import { userApi, UserApiError, isUserApiError, type DeviceAuthorizationRequest, type LoginProviderId, type OidcCallback, type SmsLoginStart } from '@/api/client'
import { withDeadline } from '@/api/deadline'
import { createStore } from 'zustand/vanilla'
import {
  assertCurrentIdentity, beginIdentityTransition, captureIdentity, completeIdentityTransition,
  getIdentityEpoch, identityKey, isCurrentIdentity, registerIdentityCleanup, type IdentityContext,
} from '@/api/request-context'
import { queryClient } from '@/app/query-client'
import { publicDeviceKey, signDeviceAuthorization, supportsSecureDeviceKey } from '@/security/deviceKey'
import { onRemoteSessionChange, publishSessionChange } from '@/stores/sessionSync'
import { clearRuntimeClaimCode } from '@/utils/runtimeClaim'
import { clearAuthenticationReturnPath, consumeAuthenticationReturnPath, safeReturnPath, storeAuthenticationReturnPath } from './return-path'

export type AuthStatus = 'uninitialized' | 'restoring' | 'anonymous' | 'authenticated' | 'transitioning' | 'expired' | 'changed-elsewhere' | 'unsupported' | 'recoverable-error'
export type AuthSnapshot = Readonly<{ status: AuthStatus; epoch: number; accountId?: string; browserSupported?: boolean; error?: unknown }>
export type PendingSmsSignIn = SmsLoginStart & { readonly flowId: string; readonly expiresAt: number; readonly resendAt: number }
export type PendingSignInApproval = OidcCallback & { readonly flowId: string } & (
  | { readonly approvalRequired?: true; readonly nonce: string; readonly clientId: string }
  | { readonly approvalRequired: false }
)
type CallbackAttempt = {
  callback: OidcCallback
  context: IdentityContext
  createdAt: number
  preparation?: Promise<PendingSignInApproval>
  completion?: Promise<AuthSnapshot>
  pending?: PendingSignInApproval
}

const authState = createStore<AuthSnapshot>(() => ({ status: 'uninitialized', epoch: getIdentityEpoch() }))
let supportPromise: Promise<boolean> | undefined
let restoration: { context: IdentityContext; promise: Promise<AuthSnapshot>; controller: AbortController } | undefined
let restoredEpoch: number | undefined
const authenticationReads = new Set<AbortController>()
let sessionTail: Promise<void> = Promise.resolve()
let sessionMutations = 0
let signOutPromise: Promise<void> | undefined
let browserStart: Promise<string> | undefined
let smsStart: Promise<PendingSmsSignIn> | undefined
let smsPending: { value: PendingSmsSignIn; context: IdentityContext; verification?: Promise<AuthSnapshot> } | undefined
const callbackAttempts = new Map<string, CallbackAttempt>()
let activeCallback: CallbackAttempt | undefined
let anonymousReason: 'anonymous' | 'expired' | 'changed-elsewhere' = 'anonymous'
let bridgeUsers = 0
let removeBridge: (() => void) | undefined

export const getAuthSnapshot = authState.getState
export const subscribeAuth = (subscriber: () => void): (() => void) => authState.subscribe(subscriber)
function update(next: Omit<AuthSnapshot, 'epoch'> & { epoch?: number }) {
  authState.setState({ ...next, epoch: next.epoch ?? getIdentityEpoch() }, true)
}
export function isAuthCancellation(reason: unknown): boolean {
  return reason instanceof Error && reason.name === 'AbortError'
}
const base64Url = (value: Uint8Array<ArrayBuffer>) => btoa(String.fromCharCode(...value)).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '')
const flowId = () => crypto.randomUUID()

registerIdentityCleanup((options) => {
  restoration?.controller.abort()
  authenticationReads.forEach((controller) => controller.abort())
  restoration = undefined
  restoredEpoch = undefined
  smsPending = undefined
  clearAuthenticationReturnPath()
  if (!options.preserveClaim) clearRuntimeClaimCode()
  for (const [id, attempt] of callbackAttempts) {
    if (attempt !== activeCallback) callbackAttempts.delete(id)
  }
  update({ status: 'transitioning', browserSupported: getAuthSnapshot().browserSupported })
})

function detectBrowserSupport(force = false): Promise<boolean> {
  if (force) supportPromise = undefined
  supportPromise ??= supportsSecureDeviceKey()
  return supportPromise
}

async function requireBrowserSupport(context: IdentityContext): Promise<void> {
  const supported = await boundedAuthenticationRead(() => detectBrowserSupport())
  assertCurrentIdentity(context)
  if (!supported) {
    update({ status: 'unsupported', browserSupported: false })
    throw new Error('secure_device_key_unsupported')
  }
}

function boundedAuthenticationRead<T>(read: (signal: AbortSignal) => Promise<T>, parent?: AbortSignal): Promise<T> {
  parent?.throwIfAborted()
  const controller = new AbortController()
  const abort = () => controller.abort(parent?.reason)
  parent?.addEventListener('abort', abort, { once: true })
  authenticationReads.add(controller)
  return withDeadline(read, controller.signal).finally(() => {
    authenticationReads.delete(controller)
    parent?.removeEventListener('abort', abort)
  })
}

async function authorization(challenge: { nonce: string; clientId: string }, profile: 'TEMPORARY' | 'TRUSTED'): Promise<DeviceAuthorizationRequest> {
  const label = 'HiveCloud Web Browser'
  const [publicKey, proof] = await Promise.all([publicDeviceKey(), signDeviceAuthorization(challenge.nonce, challenge.clientId, label, profile)])
  return { nonce: challenge.nonce, devicePublicKey: base64Url(publicKey), deviceLabel: label, sessionProfile: profile, proof: base64Url(proof) }
}

async function establish(context: IdentityContext, signal?: AbortSignal, confirmedSession?: { authenticated: boolean }): Promise<AuthSnapshot> {
  const session = confirmedSession ?? await userApi.session(signal)
  signal?.throwIfAborted()
  assertCurrentIdentity(context)
  if (!session.authenticated) {
    completeIdentityTransition()
    restoredEpoch = context.epoch
    update({ status: anonymousReason, browserSupported: true })
    return getAuthSnapshot()
  }
  const account = await userApi.account(signal)
  signal?.throwIfAborted()
  assertCurrentIdentity(context)
  if (!account.id) throw new UserApiError(502, 'invalid_account_response')
  const committed = completeIdentityTransition(account.id)
  restoredEpoch = committed.epoch
  queryClient.setQueryData(identityKey('account'), account)
  anonymousReason = 'anonymous'
  update({ status: 'authenticated', accountId: account.id, epoch: committed.epoch, browserSupported: true })
  return getAuthSnapshot()
}

function failCurrent(context: IdentityContext, reason: unknown): void {
  if (!isCurrentIdentity(context) || isAuthCancellation(reason)) return
  if (reason instanceof Error && reason.message === 'secure_device_key_unsupported') return
  if (isUserApiError(reason) && reason.status === 401) {
    anonymousReason = 'expired'
    update({ status: 'expired', error: reason, browserSupported: getAuthSnapshot().browserSupported })
  } else update({ status: 'recoverable-error', error: reason, browserSupported: getAuthSnapshot().browserSupported })
}

function restore(options: { force?: boolean; preserveClaim?: boolean } = {}): Promise<AuthSnapshot> {
  if (options.force) {
    anonymousReason = 'anonymous'
    beginIdentityTransition({ preserveClaim: options.preserveClaim })
  }
  const context = captureIdentity()
  if (restoration && isCurrentIdentity(restoration.context)) return restoration.promise
  const snapshot = getAuthSnapshot()
  if (!options.force && (((snapshot.status === 'authenticated' || snapshot.status === 'anonymous') && restoredEpoch === context.epoch) || snapshot.status === 'unsupported')) return Promise.resolve(snapshot)
  const controller = new AbortController()
  const precedingMutations = sessionTail
  const promise = withDeadline(async (signal) => {
      await precedingMutations
      signal.throwIfAborted()
      assertCurrentIdentity(context)
      const supported = await detectBrowserSupport()
      signal.throwIfAborted()
      assertCurrentIdentity(context)
      if (!supported) {
        update({ status: 'unsupported', browserSupported: false })
        return getAuthSnapshot()
      }
      return establish(context, signal)
    }, controller.signal).catch((reason: unknown) => {
    failCurrent(context, reason)
    return getAuthSnapshot()
  }).finally(() => {
    if (restoration?.promise === promise) restoration = undefined
  })
  restoration = { context, promise, controller }
  update({ status: 'restoring', browserSupported: getAuthSnapshot().browserSupported })
  return promise
}

function serializeSessionMutation<T>(operation: (signal: AbortSignal) => Promise<T>, context: () => IdentityContext | undefined): Promise<T> {
  sessionMutations += 1
  // The UI has a deadline, while the cookie-changing transport retains its
  // serialization lock until it actually settles. A late Set-Cookie must not
  // race with a replacement login. Aborted queued work cannot begin a write.
  return withDeadline((signal) => {
    const promise = sessionTail.then(() => { signal.throwIfAborted(); return operation(signal) })
    sessionTail = promise.then(() => undefined, () => undefined).finally(() => { sessionMutations -= 1 })
    return promise
  }, new AbortController().signal).catch((reason: unknown) => {
    const affectedSession = context()
    if (affectedSession) failCurrent(affectedSession, reason)
    throw reason
  })
}

function validateRedirect(value: unknown): string {
  // eslint-disable-next-line no-control-regex -- Identity redirects cannot contain control bytes or backslashes.
  if (typeof value !== 'string' || !value || /[\u0000-\u001f\u007f\\]/.test(value)) throw new UserApiError(502, 'identity_redirect_invalid')
  const base = typeof window === 'undefined' ? 'https://hive.invalid' : window.location.origin
  const url = new URL(value, base)
  if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) throw new UserApiError(502, 'identity_redirect_invalid')
  // The BFF owns the registered OIDC authorization endpoint. It may be outside
  // the console origin; console return-path rules do not apply to this URL.
  return value
}

function startBrowserAuthentication(start: (signal: AbortSignal) => Promise<{ redirect: string }>, returnPath?: string): Promise<string> {
  if (browserStart) return browserStart
  const origin = captureIdentity()
  const path = safeReturnPath(returnPath)
  let context = origin
  const promise = serializeSessionMutation(async (signal) => {
    assertCurrentIdentity(origin)
    context = beginIdentityTransition({ preserveClaim: path === '/runtime-claim' })
    storeAuthenticationReturnPath(path)
    try {
      await requireBrowserSupport(context)
      signal.throwIfAborted()
      const result = await start(signal)
      signal.throwIfAborted()
      assertCurrentIdentity(context)
      return validateRedirect(result.redirect)
    } catch (reason) { failCurrent(context, reason); throw reason }
  }, () => context).finally(() => { if (browserStart === promise) browserStart = undefined })
  browserStart = promise
  return promise
}

function startWithAuthorization(start: (proof: DeviceAuthorizationRequest) => Promise<{ redirect: string }>, returnPath?: string, requiresSession = false) {
  return startBrowserAuthentication(async (signal) => {
    const context = captureIdentity()
    if (requiresSession) {
      // The identity transition clears the old CSRF. Protected identity
      // actions still use the current browser session and need its fresh token.
      const session = await userApi.session(signal)
      signal.throwIfAborted()
      assertCurrentIdentity(context)
      if (!session.authenticated) throw new UserApiError(401, 'session_required')
    }
    const challenge = await userApi.deviceChallenge()
    signal.throwIfAborted()
    assertCurrentIdentity(context)
    const proof = await authorization(challenge, 'TEMPORARY')
    signal.throwIfAborted()
    assertCurrentIdentity(context)
    return start(proof)
  }, returnPath)
}

function startSmsSignIn(phoneNumber: string, returnPath?: string, termsAccepted = false): Promise<PendingSmsSignIn> {
  if (!termsAccepted || !/^\+861[3-9]\d{9}$/.test(phoneNumber)) return Promise.reject(new UserApiError(400, 'sms_input_invalid'))
  if (smsStart) return smsStart
  const context = captureIdentity()
  const promise = serializeSessionMutation(async (signal) => {
    assertCurrentIdentity(context)
    await requireBrowserSupport(context)
    signal.throwIfAborted()
    const challenge = await userApi.deviceChallenge()
    signal.throwIfAborted()
    assertCurrentIdentity(context)
    const proof = await authorization(challenge, 'TEMPORARY')
    signal.throwIfAborted()
    assertCurrentIdentity(context)
    const result = await userApi.smsStart(proof, phoneNumber, termsAccepted)
    signal.throwIfAborted()
    assertCurrentIdentity(context)
    if (!result.state || !result.challengeId || !Number.isFinite(result.expiresInSeconds) || result.expiresInSeconds <= 0
      || !Number.isFinite(result.resendAfterSeconds) || result.resendAfterSeconds < 0) throw new UserApiError(502, 'sms_challenge_invalid')
    const value = { ...result, flowId: flowId(), expiresAt: Date.now() + result.expiresInSeconds * 1000, resendAt: Date.now() + result.resendAfterSeconds * 1000 }
    smsPending = { value, context }
    storeAuthenticationReturnPath(returnPath)
    return value
  // A challenge only changes the SMS transaction cookie. Its failure cannot
  // invalidate the browser session already confirmed by restore().
  }, () => undefined).finally(() => { if (smsStart === promise) smsStart = undefined })
  smsStart = promise
  return promise
}

function verifySmsSignIn(pending: PendingSmsSignIn, code: string, termsAccepted = false): Promise<AuthSnapshot> {
  const attempt = smsPending
  if (!attempt || attempt.value.flowId !== pending.flowId || !isCurrentIdentity(attempt.context)) return Promise.reject(new UserApiError(409, 'sms_challenge_obsolete'))
  if (attempt.verification) return attempt.verification
  if (!termsAccepted || !/^\d{6}$/.test(code) || pending.expiresAt <= Date.now()) return Promise.reject(new UserApiError(400, 'sms_code_invalid'))
  let context = attempt.context
  let establishingSession = false
  const promise = serializeSessionMutation(async (signal) => {
    assertCurrentIdentity(attempt.context)
    // Verification does not establish a browser session. Keep the existing
    // challenge usable after an authoritative invalid-code response.
    await userApi.smsVerify(pending.state, pending.challengeId, code, termsAccepted)
    signal.throwIfAborted()
    assertCurrentIdentity(attempt.context)
    const returnPath = consumeReturnPathForTransition()
    context = beginIdentityTransition({ preserveClaim: returnPath === '/runtime-claim' })
    establishingSession = true
    storeAuthenticationReturnPath(returnPath)
    try {
      const session = await userApi.smsComplete(pending.state)
      signal.throwIfAborted()
      assertCurrentIdentity(context)
      const result = await boundedAuthenticationRead((child) => establish(context, child, session), signal)
      if (result.status !== 'authenticated') throw new UserApiError(401, 'session_not_established')
      publishSessionChange('session-changed')
      return result
    } catch (reason) { failCurrent(context, reason); throw reason }
  }, () => establishingSession ? context : undefined).catch((reason: unknown) => {
    // Only an authoritative rejection permits the same challenge to retry.
    // Unknown writes remain single-flight until the user starts a new flow.
    if (smsPending === attempt && isUserApiError(reason) && reason.status >= 400 && reason.status < 500 && reason.status !== 408) attempt.verification = undefined
    throw reason
  })
  attempt.verification = promise
  return promise
}

// Read-and-replace keeps one validated return-path value across a deliberate
// identity boundary, without allowing general cleanup to retain old drafts.
function consumeReturnPathForTransition(): string | undefined {
  return consumeAuthenticationReturnPath()
}

function prepareCallback(callback: OidcCallback): Promise<PendingSignInApproval> {
  if (!callback.code?.trim() || !callback.state?.trim()) return Promise.reject(new UserApiError(400, 'invalid_oidc_callback'))
  let entry = Array.from(callbackAttempts.entries()).find(([, attempt]) => attempt.callback.code === callback.code && attempt.callback.state === callback.state)
  if (entry?.[1].completion && entry[1].preparation) return entry[1].preparation
  if (entry && (!isCurrentIdentity(entry[1].context) || Date.now() - entry[1].createdAt > 10 * 60_000)) {
    callbackAttempts.delete(entry[0]); entry = undefined
  }
  if (entry?.[1].preparation) return entry[1].preparation
  const id = flowId()
  const attempt: CallbackAttempt = { callback: { code: callback.code, state: callback.state }, context: captureIdentity(), createdAt: Date.now() }
  callbackAttempts.set(id, attempt)
  if (callbackAttempts.size > 4) callbackAttempts.delete(callbackAttempts.keys().next().value as string)
  const precedingMutations = sessionTail
  attempt.preparation = boundedAuthenticationRead(async (signal) => {
    await precedingMutations
    signal.throwIfAborted()
    await requireBrowserSupport(attempt.context)
    let pending: PendingSignInApproval
    try {
      const challenge = await userApi.callbackChallenge(callback.state)
      if (!challenge.nonce || !challenge.clientId) throw new UserApiError(502, 'device_challenge_invalid')
      pending = { ...attempt.callback, ...challenge, flowId: id }
    } catch (reason) {
      // Only this response follows successful BFF validation of a transaction
      // whose device proof was supplied before its identity-service redirect.
      if (!isUserApiError(reason, 'callback_approval_not_pending')) throw reason
      pending = { ...attempt.callback, flowId: id, approvalRequired: false }
    }
    signal.throwIfAborted()
    assertCurrentIdentity(attempt.context)
    attempt.pending = pending
    return pending
  })
  return attempt.preparation
}

function completeCallback(pending: PendingSignInApproval, profile: 'TEMPORARY' | 'TRUSTED'): Promise<AuthSnapshot> {
  const attempt = callbackAttempts.get(pending.flowId)
  if (!attempt || attempt.pending !== pending) return Promise.reject(new UserApiError(409, 'callback_approval_not_pending'))
  if (attempt.completion) return attempt.completion
  if (!isCurrentIdentity(attempt.context) || Date.now() - attempt.createdAt > 10 * 60_000) return Promise.reject(new UserApiError(409, 'callback_approval_not_pending'))
  let context = attempt.context
  attempt.completion = serializeSessionMutation(async (signal) => {
    assertCurrentIdentity(attempt.context)
    const path = consumeReturnPathForTransition()
    activeCallback = attempt
    context = beginIdentityTransition({ preserveClaim: path === '/runtime-claim' })
    activeCallback = undefined
    storeAuthenticationReturnPath(path)
    try {
      let session: Awaited<ReturnType<typeof userApi.callback>>
      if (pending.approvalRequired === false) session = await userApi.callback(pending)
      else {
        const proof = await authorization(pending, profile)
        signal.throwIfAborted()
        assertCurrentIdentity(context)
        session = await userApi.callback(pending, proof)
      }
      signal.throwIfAborted()
      assertCurrentIdentity(context)
      const result = await boundedAuthenticationRead((child) => establish(context, child, session), signal)
      if (result.status !== 'authenticated') throw new UserApiError(401, 'session_not_established')
      publishSessionChange('session-changed')
      return result
    } catch (reason) { failCurrent(context, reason); throw reason }
  }, () => context)
  return attempt.completion
}

function signOut(): Promise<void> {
  if (signOutPromise) return signOutPromise
  const precedingMutation = sessionMutations > 0
  const context = beginIdentityTransition({ preserveCsrf: true })
  anonymousReason = 'anonymous'
  const promise = serializeSessionMutation(async (signal) => {
    assertCurrentIdentity(context)
    try {
      // An earlier callback may have changed browser cookies while its stale
      // application response was discarded. Refresh its CSRF before logout.
      if (precedingMutation) await userApi.session()
      signal.throwIfAborted()
      assertCurrentIdentity(context)
      await userApi.logout()
      signal.throwIfAborted()
      assertCurrentIdentity(context)
      update({ status: 'anonymous', browserSupported: getAuthSnapshot().browserSupported })
    } catch (reason) {
      failCurrent(context, reason)
      throw reason
    } finally {
      publishSessionChange('signed-out')
    }
  }, () => context).finally(() => { if (signOutPromise === promise) signOutPromise = undefined })
  signOutPromise = promise
  return promise
}

function invalidate(state: 'expired' | 'changed-elsewhere'): void {
  beginIdentityTransition()
  anonymousReason = state
  update({ status: state, browserSupported: getAuthSnapshot().browserSupported })
}

export function mountAuthBridge(): () => void {
  bridgeUsers += 1
  if (bridgeUsers === 1 && typeof window !== 'undefined') {
    const expired = () => {
      if (getAuthSnapshot().status !== 'authenticated') return
      invalidate('expired')
      publishSessionChange('session-expired')
    }
    window.addEventListener('hivecloud:user-auth-expired', expired)
    const remote = onRemoteSessionChange(() => {
      invalidate('changed-elsewhere')
      void restore()
    })
    removeBridge = () => { window.removeEventListener('hivecloud:user-auth-expired', expired); remote() }
  }
  let removed = false
  return () => {
    if (removed) return
    removed = true
    bridgeUsers -= 1
    if (bridgeUsers === 0) { removeBridge?.(); removeBridge = undefined }
  }
}

export const authService = {
  restore, signOut, detectBrowserSupport, prepareCallback, completeCallback, startSmsSignIn, verifySmsSignIn,
  signIn: (returnPath?: string) => startBrowserAuthentication(() => userApi.login(), returnPath),
  signInWithProvider: (provider: LoginProviderId, returnPath?: string) => startBrowserAuthentication(() => userApi.providerLogin(provider), returnPath),
  register: (returnPath?: string) => startWithAuthorization((proof) => userApi.register(proof), returnPath),
  stepUp: (returnPath: string) => startWithAuthorization((proof) => userApi.stepUp(proof), returnPath, true),
  enrollMfa: (returnPath = '/account#mfa') => startWithAuthorization((proof) => userApi.enrollMfa(proof), returnPath, true),
  manageMfaRecoveryCodes: (returnPath = '/account#mfa') => startWithAuthorization((proof) => userApi.manageMfaRecoveryCodes(proof), returnPath, true),
  cancelSmsSignIn: () => { smsPending = undefined; clearAuthenticationReturnPath() },
  invalidate,
}
